import { useState, useMemo, useCallback } from 'react';
import { withBackNavigation } from '@/lib/withBackNavigation';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { X, Lock, AlertCircle, Pencil, Check } from 'lucide-react';
import { ENV } from '@/config/environment';
import { getAppOrigin } from '@/lib/capacitor';
import { usePaymentStateMachine } from '@/hooks/usePaymentStateMachine';
import { PaymentResult } from '@/components/ui/PaymentResult';
import { NumericInput } from '@/components/ui/NumericInput';
import { amountDueNow, roundCurrency } from '@/lib/invoiceCalculations';

interface PublicPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Public create-payment-intent endpoint of the invoice or estimate portal (token already in the path). */
  createIntentUrl: string;
  connectedAccountId: string;
  totalAmount: number;
  amountPaid: number;
  /** Deposit the business asked for; the amount defaults to what's still owed on it. */
  depositAmount?: number | null;
  /** "Invoice" or "Estimate", for the summary label. */
  documentLabel?: string;
  onSuccess: (paidAmount: number) => void;
}

/**
 * Public payment modal for invoice and estimate payments via Stripe.
 * No authentication required — uses the public portal token.
 * Uses the deferred-intent pattern (same as StripePaymentModal).
 */
function PublicPaymentModalBase({
  isOpen,
  onClose,
  createIntentUrl,
  connectedAccountId,
  totalAmount,
  amountPaid,
  depositAmount,
  documentLabel = 'Invoice',
  onSuccess,
}: PublicPaymentModalProps) {
  // Hooks before the early return (rules of hooks); Stripe loads only once the modal opens.
  const stripePromise = useMemo(() => {
    if (!isOpen || !ENV.STRIPE_PUBLISHABLE_KEY) return null;

    return loadStripe(ENV.STRIPE_PUBLISHABLE_KEY, {
      stripeAccount: connectedAccountId,
    });
  }, [isOpen, connectedAccountId]);

  if (!isOpen) return null;

  const remainingBalance = Math.round((totalAmount - amountPaid) * 100) / 100;
  const dueNow = amountDueNow(totalAmount, amountPaid, depositAmount);
  // Once the deposit is met the customer is offered the rest of the balance
  const defaultAmount = dueNow > 0 ? dueNow : remainingBalance;

  if (!ENV.STRIPE_PUBLISHABLE_KEY) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 relative">
          <button
            onClick={onClose}
            className="absolute right-4 top-4 text-gray-400 hover:text-gray-500 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
          <div className="text-center space-y-3 pt-2">
            <AlertCircle className="h-10 w-10 text-red-500 mx-auto" />
            <h3 className="text-lg font-semibold text-gray-900">Payment Unavailable</h3>
            <p className="text-gray-600">Online payments are temporarily unavailable.</p>
            <button
              onClick={onClose}
              className="w-full mt-4 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto keyboard-aware-overlay">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg relative flex flex-col max-h-[90vh] keyboard-constrained-modal">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100 shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">Secure Payment</h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-500 hover:bg-gray-100 rounded-full transition-all"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto">
          <Elements
            stripe={stripePromise}
            options={{
              mode: 'payment',
              amount: Math.round(defaultAmount * 100),
              currency: 'usd',
              appearance: {
                theme: 'stripe' as const,
                variables: { colorPrimary: '#2563eb' },
              },
            }}
          >
            <PublicPaymentForm
              createIntentUrl={createIntentUrl}
              totalAmount={totalAmount}
              amountPaid={amountPaid}
              remainingBalance={remainingBalance}
              depositAmount={depositAmount}
              dueNow={dueNow}
              defaultAmount={defaultAmount}
              documentLabel={documentLabel}
              onSuccess={onSuccess}
              onClose={onClose}
            />
          </Elements>
        </div>
      </div>
    </div>
  );
}

function PublicPaymentForm({
  createIntentUrl,
  totalAmount,
  amountPaid,
  remainingBalance,
  depositAmount,
  dueNow,
  defaultAmount,
  documentLabel,
  onSuccess,
  onClose,
}: {
  createIntentUrl: string;
  totalAmount: number;
  amountPaid: number;
  remainingBalance: number;
  depositAmount?: number | null;
  dueNow: number;
  defaultAmount: number;
  documentLabel: string;
  onSuccess: (paidAmount: number) => void;
  onClose: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const { state, errorMessage, transitionTo, reset, isFormDisabled } = usePaymentStateMachine();

  const [isEditing, setIsEditing] = useState(false);
  const [editAmount, setEditAmount] = useState(defaultAmount.toFixed(2));
  const [currentAmount, setCurrentAmount] = useState(defaultAmount);
  const [localError, setLocalError] = useState<string | null>(null);

  const handleSaveAmount = () => {
    const newAmount = parseFloat(editAmount);
    if (isNaN(newAmount) || newAmount <= 0) {
      setLocalError('Please enter a valid amount greater than 0');
      return;
    }
    if (newAmount > remainingBalance) {
      setLocalError(`Amount cannot exceed remaining balance of $${remainingBalance.toFixed(2)}`);
      return;
    }
    setCurrentAmount(newAmount);
    setIsEditing(false);
    setLocalError(null);
  };

  /**
   * Confirm Payment — deferred-intent flow:
   *   1. Validate card input with elements.submit()
   *   2. Create PaymentIntent via public endpoint (no auth)
   *   3. Confirm with Stripe client-side
   */
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (state !== 'idle' || !stripe || !elements || isEditing) {
      return;
    }

    transitionTo('processing');
    setLocalError(null);

    try {
      // Step 1: Validate the PaymentElement form
      const { error: submitError } = await elements.submit();
      if (submitError) {
        transitionTo('failed', submitError.message ?? 'Please check your card details.');
        return;
      }

      // Step 2: Create PaymentIntent via public API (no auth headers needed)
      const response = await fetch(
        createIntentUrl,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ amount: Math.round(currentAmount * 100) / 100 }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || errorData?.message || 'Failed to create payment intent');
      }

      const { client_secret } = await response.json();

      // Step 3: Confirm with Stripe
      const result = await stripe.confirmPayment({
        elements,
        clientSecret: client_secret,
        confirmParams: {
          return_url: getAppOrigin() + '/payment-complete',
        },
        redirect: 'if_required',
      });

      if (result.error) {
        transitionTo('failed', result.error.message ?? 'An unexpected error occurred.');
      } else if (
        result.paymentIntent?.status === 'succeeded' ||
        result.paymentIntent?.status === 'processing'
      ) {
        transitionTo('succeeded');
      } else {
        transitionTo('failed', 'Payment processing incomplete. Please try again.');
      }
    } catch (err: any) {
      transitionTo('failed', err.message || 'An unexpected error occurred during processing.');
    }
  };

  const handleRetry = useCallback(() => {
    reset();
    setLocalError(null);
  }, [reset]);

  // ── Render based on state ──

  if (state === 'succeeded') {
    return (
      <PaymentResult
        variant="success"
        amount={currentAmount}
        primaryLabel="Done"
         onPrimaryAction={() => onSuccess(currentAmount)}
      />
    );
  }

  if (state === 'failed') {
    return (
      <PaymentResult
        variant="error"
        amount={currentAmount}
        message={errorMessage ?? undefined}
        primaryLabel="Try Again"
        onPrimaryAction={handleRetry}
        secondaryLabel="Close"
        onSecondaryAction={onClose}
      />
    );
  }

  const displayError = localError || errorMessage;
  const isProcessing = state === 'processing';

  return (
    <div className="relative">
      {isProcessing && (
        <div className="absolute inset-0 z-20 bg-white/80 backdrop-blur-[2px] rounded-lg flex items-center justify-center">
          <PaymentResult variant="processing" amount={currentAmount} />
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Invoice summary */}
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
          <div className="flex justify-between text-sm text-gray-600 mb-1">
            <span>{documentLabel} Total</span>
            <span className="font-medium">${totalAmount.toFixed(2)}</span>
          </div>
          {amountPaid > 0 && (
            <div className="flex justify-between text-sm text-gray-600 mb-1">
              <span>Already Paid</span>
              <span className="font-medium text-green-600">-${amountPaid.toFixed(2)}</span>
            </div>
          )}
          {depositAmount != null && dueNow > 0 && dueNow < remainingBalance && (
            <>
              <div className="flex justify-between text-sm text-gray-600 mb-1">
                <span>Balance</span>
                <span className="font-medium">${remainingBalance.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm font-medium text-blue-700 mb-1">
                <span>Down payment due now</span>
                <span>${dueNow.toFixed(2)}</span>
              </div>
            </>
          )}
          {depositAmount != null && dueNow === 0 && remainingBalance > 0 && (
            <div className="flex justify-between text-sm text-green-600 mb-1">
              <span>Down payment received</span>
              <Check className="h-4 w-4" />
            </div>
          )}
          <div className="flex justify-between items-center pt-2 border-t border-gray-200 mt-2">
            <span className="text-sm font-medium text-gray-700">Amount to Pay</span>

            {isEditing ? (
              <div className="flex items-center gap-2">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">$</span>
                  <NumericInput
                    value={editAmount === '' ? undefined : Number(editAmount)}
                    onChange={(val) => setEditAmount(val === undefined ? '' : String(val))}
                    className="pl-6 w-32 py-1 px-2 text-right font-bold text-gray-900 border rounded-md focus:ring-2 focus:ring-blue-500 outline-none"
                    step="0.01"
                    min="0.01"
                    max={remainingBalance}
                    autoFocus
                  />
                </div>
                <button
                  type="button"
                  onClick={handleSaveAmount}
                  className="p-1.5 text-green-600 hover:bg-green-50 rounded-full transition-colors"
                  title="Save amount"
                >
                  <Check className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsEditing(false);
                    setEditAmount(currentAmount.toFixed(2));
                    setLocalError(null);
                  }}
                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                  title="Cancel"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-xl font-bold text-gray-900">${currentAmount.toFixed(2)}</span>
                <button
                  type="button"
                  onClick={() => setIsEditing(true)}
                  className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-all"
                  title="Edit amount"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Stripe Payment Element */}
        <PaymentElement />

        {displayError && (
          <div className="p-3 bg-red-50 text-red-700 text-sm rounded-md flex items-center gap-2">
            <AlertCircle className="h-4 w-4 shrink-0" />
            {displayError}
          </div>
        )}

        <button
          type="submit"
          disabled={!stripe || isFormDisabled || isEditing}
          className="w-full flex justify-center items-center gap-2 min-h-[44px] py-2.5 px-4 border border-transparent rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
        >
          {`Pay $${currentAmount.toFixed(2)} `}
          <Lock className="h-4 w-4 opacity-80" />
        </button>

        <p className="text-xs text-center text-gray-500 mt-2">
          Payments are securely processed by Stripe.
        </p>
      </form>
    </div>
  );
}

export const PublicPaymentModal = withBackNavigation(PublicPaymentModalBase);

/** Tells the customer how much of the balance the business wants now. Renders nothing without a deposit. */
export function DepositDueNotice({ total, amountPaid, depositAmount }: {
  total: number;
  amountPaid: number;
  depositAmount?: number | null;
}) {
  const balance = roundCurrency(total - amountPaid);
  if (depositAmount == null || balance <= 0) return null;
  const dueNow = amountDueNow(total, amountPaid, depositAmount);

  return (
    <div className="mt-4 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
      {dueNow > 0 ? (
        <>Down payment due now: <strong>${dueNow.toFixed(2)}</strong> (balance ${balance.toFixed(2)})</>
      ) : (
        <>Down payment received. Remaining balance: <strong>${balance.toFixed(2)}</strong></>
      )}
    </div>
  );
}
